import BN from "bignumber.js";
import { dictToB64 } from "@terra-money/terra.js/dist/util/contract";
import {
  columbus4,
  AddressProviderFromJson,
  MARKET_DENOMS,
} from "@anchor-protocol/anchor.js";

const addressProvider = new AddressProviderFromJson(columbus4);

const mutationTypes = {
  SET_BALANCES: "anchor/SET_BALANCES",
  SET_EXCHANGE_RATE: "anchor/SET_EXCHANGE_RATE",
};

const state = {
  balances: {
    available: null,
    deposited: null,
  },
  exchangeRate: {
    value: null,
  },
};

const actions = {
  async getDepositRawTx(
    { rootGetters, dispatch },
    { amount, memo, gasPriceStep }
  ) {
    await dispatch("staking/tendermint/getAccountInfo", null, { root: true });
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    const { accountNumber, sequence } = rootGetters[
      "staking/tendermint/accountInfo"
    ].result;
    const { chainId } = rootGetters["staking/tendermint/blockInfo"].result;
    gasPriceStep = gasPriceStep || project.staking.anchor.gasPriceStep.medium;
    return stakeUtils.txBuilder.getRawTx("executeContract", {
      sender: wallet.address,
      contract: addressProvider.data.mmMarket,
      coins: [
        {
          amount: new BN(amount)
            .multipliedBy(project.staking.conversionRate)
            .toFixed(0, 1),
          denom: MARKET_DENOMS.UUSD,
        },
      ],
      executeMsg: {
        deposit_stable: {},
      },
      chainId,
      feeDenom: MARKET_DENOMS.UUSD,
      gas: project.staking.anchor.gas.Deposit,
      gasPrice: new BN(project.staking.anchor.gas.Deposit)
        .multipliedBy(gasPriceStep)
        .toFixed(0),
      memo,
      accountNumber,
      sequence,
    });
  },
  async getWithdrawalRawTx(
    { state, rootGetters, dispatch },
    { amount, memo, gasPriceStep }
  ) {
    await Promise.all([
      dispatch("staking/tendermint/getAccountInfo", null, { root: true }),
      dispatch("fetchExchangeRate"),
    ]);
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    const { accountNumber, sequence } = rootGetters[
      "staking/tendermint/accountInfo"
    ].result;
    const { chainId } = rootGetters["staking/tendermint/blockInfo"].result;
    gasPriceStep = gasPriceStep || project.staking.anchor.gasPriceStep.medium;
    amount = new BN(amount).dividedBy(state.exchangeRate.value).toNumber();
    return stakeUtils.txBuilder.getRawTx("executeContract", {
      sender: wallet.address,
      contract: addressProvider.data.aTerra,
      coins: [],
      executeMsg: {
        send: {
          contract: addressProvider.data.mmMarket,
          amount: new BN(amount)
            .multipliedBy(project.staking.conversionRate)
            .toFixed(0, 1),
          msg: dictToB64({
            redeem_stable: {},
          }),
        },
      },
      chainId,
      feeDenom: MARKET_DENOMS.UUSD,
      gas: project.staking.anchor.gas.Withdraw,
      gasPrice: new BN(project.staking.anchor.gas.Withdraw)
        .multipliedBy(gasPriceStep)
        .toFixed(0),
      memo,
      accountNumber,
      sequence,
    });
  },
  async fetchTotalDeposit({ rootGetters }) {
    const address = rootGetters["userWallets/walletSelected"].address;
    const blockHeight =
      rootGetters["staking/tendermint/blockInfo"].result.blockHeight;
    try {
      const { data } = await fetch(
        `https://mantle.terra.dev/?__totalDeposit`,
        {
          method: "POST",
          body: JSON.stringify({
            operationName: "__totalDeposit",
            variables: {
              anchorTokenContract: addressProvider.data.aTerra,
              anchorTokenBalanceQuery: `{"balance":{"address":"${address}"}}`,
              moneyMarketContract: addressProvider.data.mmMarket,
              moneyMarketEpochQuery: `{"epoch_state":{"block_height":${blockHeight}}}`,
            },
            query:
              "query __totalDeposit($anchorTokenContract: String!, $anchorTokenBalanceQuery: String!, $moneyMarketContract: String!, $moneyMarketEpochQuery: String!) {\n  aUSTBalance: WasmContractsContractAddressStore(\n    ContractAddress: $anchorTokenContract\n    QueryMsg: $anchorTokenBalanceQuery\n  ) {\n    Result\n    __typename\n  }\n  exchangeRate: WasmContractsContractAddressStore(\n    ContractAddress: $moneyMarketContract\n    QueryMsg: $moneyMarketEpochQuery\n  ) {\n    Result\n    __typename\n  }\n}\n",
          }),
        }
      ).then(res => res.json());
      return {
        deposited: JSON.parse(data.aUSTBalance.Result).balance,
        exchangeRate: JSON.parse(data.exchangeRate.Result).exchange_rate,
      };
    } catch (error) {
      return { deposited: 0, exchangeRate: 1 };
    }
  },
  async fetchBalances({ commit, rootGetters, dispatch }) {
    let { deposited, exchangeRate } = await dispatch("fetchTotalDeposit");
    deposited = new BN(deposited).multipliedBy(exchangeRate).toNumber();
    const available =
      rootGetters["staking/tendermint/accountInfo"].result.balances.find(
        bl => bl.denom === MARKET_DENOMS.UUSD
      )?.value ?? 0;
    commit(mutationTypes.SET_BALANCES, { available, deposited });
    commit(mutationTypes.SET_EXCHANGE_RATE, { value: exchangeRate });
  },
  async fetchExchangeRate({ dispatch, commit }) {
    let { exchangeRate } = await dispatch("fetchTotalDeposit");
    commit(mutationTypes.SET_EXCHANGE_RATE, { value: exchangeRate });
  },
  async fetchRequiredInfo({ dispatch }) {
    return Promise.all([dispatch("fetchBalances")]);
  },
};

const mutations = {
  [mutationTypes.SET_BALANCES](state, data) {
    state.balances = data;
  },
  [mutationTypes.SET_EXCHANGE_RATE](state, data) {
    state.exchangeRate = data;
  },
};

const getters = {
  balances: state => state.balances,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
